/**
 * Page blocks.
 *
 * A page has always been "one HTML blob plus exactly one gallery", which is why
 * a second gallery, or a video between two text sections, was impossible. Blocks
 * make that an ordered list instead.
 *
 * Nothing is migrated. A page with no blocks is compiled into the equivalent two
 * blocks on read (see blocksFromLegacy), so every existing page keeps rendering
 * through the same components as before. Blocks are only stored once a page is
 * saved from the new canvas.
 */

import { hasActiveFilter } from './filter.js';
import { COLUMNS_WIDTH } from './columns.js';
import { normalizeSlideshowSettings } from '../slideshow/settings.js';

const isPercent = (value) => Number.isFinite(value) && value >= 0 && value <= 100;

/** A free image position inside its page, or null when it sits at its anchor. */
const positionOf = (image) => (
    isPercent(image?.x) && isPercent(image?.y) ? { x: image.x, y: image.y } : null
);

export const BLOCK_TYPES = {
    RICHTEXT: 'richtext',
    GALLERY: 'gallery',
    EMBED: 'embed',
    COLUMNS: 'columns',
    SPACER: 'spacer',
    UNLAYER: 'unlayer',
    VECTOR: 'vector',
};

/** Layouts a gallery block can render its items with. */
export const GALLERY_LAYOUTS = {
    MASONRY: 'masonry',
    SLIDESHOW: 'slideshow',
    DIRECTORY: 'directory',
    BACKGROUND: 'background',
};

/**
 * Item kinds a gallery block can hold. Mixing them is the point: a directory can
 * contain page tiles, stills and video side by side.
 */
export const ITEM_TYPES = {
    IMAGE: 'image',
    SPREAD: 'spread',
    VIDEO: 'video',
    YOUTUBE: 'youtube',
    PAGE: 'page',
};

export const BLOCK_PALETTE = [
    { type: BLOCK_TYPES.RICHTEXT, label: 'Text', hint: 'Words, headings and links' },
    { type: BLOCK_TYPES.GALLERY, label: 'Gallery', hint: 'Images, video or linked pages' },
    { type: BLOCK_TYPES.EMBED, label: 'Embed', hint: 'A YouTube or Vimeo link' },
    { type: BLOCK_TYPES.COLUMNS, label: 'Columns', hint: 'Texts side by side, set per device' },
    { type: BLOCK_TYPES.SPACER, label: 'Spacer', hint: 'Vertical breathing room' },
    { type: BLOCK_TYPES.VECTOR, label: 'Vector', hint: 'Brand SVG, tinted or original colours' },
];

/** What "add a block" offers. Masonry and directory are both gallery blocks. */
export const INSERT_PALETTE = [
    { type: BLOCK_TYPES.RICHTEXT, label: 'Text', hint: 'Words, headings and links' },
    { type: BLOCK_TYPES.COLUMNS, label: 'Columns', hint: 'Texts side by side, set per device' },
    { type: BLOCK_TYPES.VECTOR, label: 'Vector', hint: 'Brand SVG, tinted or original colours' },
    { type: BLOCK_TYPES.SPACER, label: 'Spacer', hint: 'Vertical breathing room' },
    { type: BLOCK_TYPES.EMBED, label: 'Embed', hint: 'A YouTube or Vimeo link' },
    { type: BLOCK_TYPES.GALLERY, layout: GALLERY_LAYOUTS.MASONRY, label: 'Masonry', hint: 'A masonry of images' },
    { type: BLOCK_TYPES.GALLERY, layout: GALLERY_LAYOUTS.DIRECTORY, label: 'Directory', hint: 'A directory of linked pages' },
];

/** Fullscreen / slideshow overlay: text and vectors over the background. */
export const OVERLAY_INSERT_PALETTE = [
    { type: BLOCK_TYPES.RICHTEXT, label: 'Text', hint: 'Words over the background' },
    { type: BLOCK_TYPES.VECTOR, label: 'Vector', hint: 'Brand SVG over the background' },
];

let counter = 0;
/** Ids only need to be unique within a page. */
export function newBlockId(type = 'block') {
    counter += 1;
    return `${type}-${Date.now().toString(36)}-${counter}`;
}

/**
 * A new block of the given type, with the defaults that make it renderable.
 * @param {string} type
 * @param {Object} [overrides]
 * @returns {Object}
 */
export function createBlock(type, overrides = {}) {
    const base = { id: newBlockId(type), type };

    switch (type) {
        case BLOCK_TYPES.RICHTEXT:
            return { ...base, html: '', maxWidth: 65, ...overrides };
        case BLOCK_TYPES.GALLERY:
            return {
                ...base,
                layout: GALLERY_LAYOUTS.MASONRY,
                items: [],
                columns: 5,
                breakpoints: null,
                gap: 4,
                spreadGap: 0,
                crops: {},
                // Directory tiles have always swapped images instantly; a crossfade
                // is opt-in per block so existing pages do not start animating.
                cycleCrossfade: false,
                ...overrides,
            };
        case BLOCK_TYPES.EMBED:
            return { ...base, provider: 'youtube', url: '', posterFileId: null, ...overrides };
        case BLOCK_TYPES.COLUMNS:
            return {
                ...base,
                maxWidth: COLUMNS_WIDTH.fallback,
                cells: [
                    createBlock(BLOCK_TYPES.RICHTEXT),
                    createBlock(BLOCK_TYPES.RICHTEXT),
                ],
                ...overrides,
            };
        case BLOCK_TYPES.SPACER:
            return { ...base, height: 4, ...overrides };
        case BLOCK_TYPES.UNLAYER:
            return { ...base, designJson: null, html: '', ...overrides };
        case BLOCK_TYPES.VECTOR:
            return {
                ...base,
                // null inherits the brand page logo; a fileId overrides it.
                fileId: null,
                inheritTextColour: true,
                width: { value: 80, unit: 'vw' },
                height: 'auto',
                align: 'center',
                hrefPageId: null,
                alt: '',
                ...overrides,
            };
        default:
            return { ...base, ...overrides };
    }
}

/**
 * Normalise gallery.images, which has three historical shapes:
 * a bare id, an object with settings, or a two-element array for a spread.
 *
 * @param {Array} images - gallery.images
 * @returns {Array} Items in the new tagged form
 */
export function itemsFromGalleryImages(images) {
    if (!Array.isArray(images)) return [];

    return images.map((entry) => {
        if (Array.isArray(entry) && entry.length === 2) {
            return {
                type: ITEM_TYPES.SPREAD,
                sides: entry.map((side) => (
                    typeof side === 'string'
                        ? { fileId: side }
                        : { fileId: side?.id, scale: side?.scale, anchor: side?.anchor, fill: side?.fill, ...positionOf(side) }
                )),
            };
        }

        if (typeof entry === 'string') {
            return { type: ITEM_TYPES.IMAGE, fileId: entry };
        }

        if (entry && typeof entry === 'object') {
            return {
                type: ITEM_TYPES.IMAGE,
                fileId: entry.id,
                ...(entry.scale !== undefined ? { scale: entry.scale } : {}),
                ...(entry.anchor !== undefined ? { anchor: entry.anchor } : {}),
                ...(entry.fill !== undefined ? { fill: entry.fill } : {}),
                ...positionOf(entry),
            };
        }

        return null;
    }).filter(Boolean);
}

/**
 * Turn items back into the gallery.images shape the existing renderers and API
 * expect, so a page edited as blocks is still readable by everything else.
 *
 * Items that have no equivalent (video, youtube, page tiles) are skipped here and
 * carried in the block instead.
 *
 * @param {Array} items
 * @returns {Array} gallery.images
 */
export function galleryImagesFromItems(items) {
    if (!Array.isArray(items)) return [];

    return items.map((item) => {
        if (item?.type === ITEM_TYPES.SPREAD) {
            return (item.sides || []).map((side) => {
                // A side with no settings was stored as a bare id, and has to go
                // back that way: the existing renderers and saved data both use
                // that shape, and rewriting it would be a silent data change.
                const hasSettings = side.scale !== undefined
                    || side.anchor !== undefined
                    || side.fill !== undefined
                    || positionOf(side) !== null;
                if (!hasSettings) return side.fileId;

                return {
                    id: side.fileId,
                    ...(side.scale !== undefined ? { scale: side.scale } : {}),
                    ...(side.anchor !== undefined ? { anchor: side.anchor } : {}),
                    ...(side.fill !== undefined ? { fill: side.fill } : {}),
                    ...positionOf(side),
                };
            });
        }

        if (item?.type === ITEM_TYPES.IMAGE) {
            const hasSettings = item.scale !== undefined
                || item.anchor !== undefined
                || item.fill !== undefined
                || positionOf(item) !== null;
            if (!hasSettings) return item.fileId;
            return {
                id: item.fileId,
                ...(item.scale !== undefined ? { scale: item.scale } : {}),
                ...(item.anchor !== undefined ? { anchor: item.anchor } : {}),
                ...(item.fill !== undefined ? { fill: item.fill } : {}),
                ...positionOf(item),
            };
        }

        return null;
    }).filter(Boolean);
}

/** gallery.links is stored either as an array or keyed by pageId. */
function normalizeLinks(links) {
    if (Array.isArray(links)) return links;
    if (links && typeof links === 'object') {
        return Object.entries(links).map(([pageId, value]) => ({
            pageId,
            ...(value && typeof value === 'object' ? value : {}),
        }));
    }
    return [];
}

/**
 * Compile a legacy page into blocks.
 *
 * At most two: the HTML content, then the gallery. The result renders through the
 * same components as before, so an untouched page is unchanged.
 *
 * @param {Object} page - A page or the home object
 * @returns {Array} Blocks
 */
export function blocksFromLegacy(page) {
    if (!page) return [];
    if (Array.isArray(page.blocks) && page.blocks.length > 0) return page.blocks;

    const blocks = [];
    const galleryType = page.gallery?.type || 'none';

    // Slideshow renders its own text overlay, so the HTML belongs to that block
    // rather than standing on its own above it.
    const htmlBelongsToGallery = galleryType === GALLERY_LAYOUTS.SLIDESHOW;

    if (page.content && !htmlBelongsToGallery) {
        blocks.push({
            id: 'legacy-richtext',
            type: BLOCK_TYPES.RICHTEXT,
            html: page.content,
            maxWidth: page.contentMaxWidth ?? 65,
        });
    }

    if (galleryType !== 'none') {
        const gallery = page.gallery || {};
        blocks.push({
            id: 'legacy-gallery',
            type: BLOCK_TYPES.GALLERY,
            layout: galleryType,
            items: itemsFromGalleryImages(gallery.images),
            links: normalizeLinks(gallery.links),
            columns: gallery.columns,
            breakpoints: gallery.breakpoints,
            gap: gallery.gap,
            spreadGap: gallery.spreadGap,
            crops: gallery.crops || {},
            cycleCrossfade: false,
            // Slideshow needs the intro text it used to read straight off the page.
            ...(htmlBelongsToGallery ? { html: page.content, maxWidth: page.contentMaxWidth ?? 65 } : {}),
            ...(galleryType === GALLERY_LAYOUTS.BACKGROUND
                ? { backgroundImage: page.backgroundImage || null, html: page.content }
                : {}),
        });
    }

    return blocks;
}

/**
 * Text and vectors for fullscreen / slideshow overlays.
 *
 * Once page.blocks is an array, it is the source of truth (Text can be removed,
 * and more than one Text can exist). page.content mirrors the first richtext
 * for the legacy Html path / inline editor. With no blocks array yet, synthesize
 * a single Text row from page.content.
 */
export function overlayBlocks(page) {
    const type = page?.gallery?.type || 'none';
    if (type === GALLERY_LAYOUTS.SLIDESHOW) return slideshowBlocks(page);
    if (type !== GALLERY_LAYOUTS.BACKGROUND) return null;

    const text = {
        id: 'legacy-richtext',
        type: BLOCK_TYPES.RICHTEXT,
        html: page.content || '',
        maxWidth: page.contentMaxWidth ?? 65,
    };

    // undefined/null → never stored as overlay blocks; synthesize Text.
    // [] → user cleared the overlay; keep it empty (do not re-insert Text).
    if (!Array.isArray(page.blocks)) {
        return [text];
    }

    const list = page.blocks.filter(
        (block) => block.type === BLOCK_TYPES.RICHTEXT || block.type === BLOCK_TYPES.VECTOR,
    );
    const richtexts = list.filter((block) => block.type === BLOCK_TYPES.RICHTEXT);
    // Single text row: page.content is still canonical (inline Html editor).
    if (richtexts.length === 1) {
        const onlyId = richtexts[0].id;
        return list.map((block) => (
            block.id === onlyId
                ? {
                    ...block,
                    html: page.content || '',
                    maxWidth: page.contentMaxWidth ?? block.maxWidth ?? 65,
                }
                : block
        ));
    }
    // Vectors stored without a text row, but page.content still has copy —
    // keep that copy visible until the user clears/removes it (content === '').
    if (richtexts.length === 0 && String(page.content || '').trim()) {
        return [text, ...list];
    }
    return list;
}

/**
 * The slideshow's Text and Vector blocks: the one store for its text.
 *
 * A page saved with `page.slideshow` settings stores its blocks as they are.
 * Older pages are read the way they always rendered: with no blocks, one Text
 * from page.content; with one Text, page.content was the copy being edited;
 * with vectors only, page.content still showed above them.
 */
export function slideshowBlocks(page) {
    if (!page) return [];
    const text = (html) => ({
        id: 'legacy-richtext',
        type: BLOCK_TYPES.RICHTEXT,
        html: html || '',
        maxWidth: page.contentMaxWidth ?? 65,
    });
    if (!Array.isArray(page.blocks)) return [text(page.content)];

    const list = page.blocks.filter(
        (block) => block?.type === BLOCK_TYPES.RICHTEXT || block?.type === BLOCK_TYPES.VECTOR,
    );
    if (page.slideshow && typeof page.slideshow === 'object') return list;

    const richtexts = list.filter((block) => block.type === BLOCK_TYPES.RICHTEXT);
    if (richtexts.length === 1) {
        const onlyId = richtexts[0].id;
        return list.map((block) => (
            block.id === onlyId
                ? { ...block, html: page.content || '', maxWidth: page.contentMaxWidth ?? block.maxWidth ?? 65 }
                : block
        ));
    }
    if (richtexts.length === 0 && String(page.content || '').trim()) {
        return [text(page.content), ...list];
    }
    return list;
}

/** page.content for a slideshow: its first Text, for search, share cards and older readers. */
export function slideshowContent(blocks) {
    const text = (blocks || []).find((block) => block?.type === BLOCK_TYPES.RICHTEXT);
    return text ? (text.html ?? '') : '';
}

/**
 * A slideshow draft holds its text only as blocks, with its settings, so the
 * editor edits exactly what visitors see. Saving writes both.
 */
export function slideshowDraft(draft, settings = draft?.slideshow) {
    if (!draft) return draft;
    const blocks = JSON.parse(JSON.stringify(slideshowBlocks({ ...draft, slideshow: settings })));
    return {
        ...draft,
        blocks,
        content: slideshowContent(blocks),
        slideshow: normalizeSlideshowSettings(settings),
    };
}

/** @deprecated Use overlayBlocks — kept as an alias during the cutover. */
export function overlayTextBlocks(page) {
    return overlayBlocks(page);
}

/**
 * Fold blocks back into the legacy page fields.
 *
 * Only the first richtext and the first gallery can round-trip, which is exactly
 * what a legacy page holds. Extra blocks are kept in page.blocks and are what
 * makes the new canvas worth having.
 *
 * @param {Array} blocks
 * @returns {Object} { content, contentMaxWidth, gallery }
 */
export function legacyFromBlocks(blocks) {
    const list = Array.isArray(blocks) ? blocks : [];
    // Text split into columns still names the page (search, share cards) by its first column.
    const columns = list.find((block) => block.type === BLOCK_TYPES.COLUMNS);
    const firstColumn = columns?.cells?.find((cell) => cell?.type === BLOCK_TYPES.RICHTEXT);
    const richtext = list.find((block) => block.type === BLOCK_TYPES.RICHTEXT)
        || (firstColumn && { ...firstColumn, maxWidth: columns.maxWidth ?? firstColumn.maxWidth });
    const gallery = list.find((block) => block.type === BLOCK_TYPES.GALLERY);

    const out = {
        content: richtext?.html ?? gallery?.html ?? '',
        contentMaxWidth: richtext?.maxWidth ?? gallery?.maxWidth ?? 65,
    };

    if (gallery) {
        out.gallery = {
            type: gallery.layout || GALLERY_LAYOUTS.MASONRY,
            images: galleryImagesFromItems(gallery.items),
            links: gallery.links || [],
            columns: gallery.columns,
            breakpoints: gallery.breakpoints,
            gap: gallery.gap,
            spreadGap: gallery.spreadGap,
            crops: gallery.crops || null,
        };
    }

    return out;
}

/**
 * Put new page.content into the block it mirrors (the first text, else the
 * first gallery's intro text), for writers that only know the legacy field.
 * Without this a page stored as blocks keeps showing the old text.
 * @param {Array} blocks
 * @param {string} html
 * @returns {Array} blocks, unchanged when nothing mirrors content
 */
export function syncContentIntoBlocks(blocks, html) {
    if (!Array.isArray(blocks) || typeof html !== 'string') return blocks;
    const source = blocks.find((block) => block?.type === BLOCK_TYPES.RICHTEXT)
        || blocks.find((block) => block?.type === BLOCK_TYPES.GALLERY && typeof block.html === 'string');
    if (!source || (source.html || '') === html) return blocks;
    return blocks.map((block) => (block === source ? { ...block, html } : block));
}

/**
 * Items a legacy gallery cannot express, so the caller knows when a page can no
 * longer be represented by the old fields alone.
 * @param {Array} blocks
 * @returns {boolean}
 */
export function needsBlockStorage(blocks) {
    const list = Array.isArray(blocks) ? blocks : [];

    const richtextCount = list.filter((b) => b.type === BLOCK_TYPES.RICHTEXT).length;
    const galleryCount = list.filter((b) => b.type === BLOCK_TYPES.GALLERY).length;
    if (richtextCount > 1 || galleryCount > 1) return true;

    if (list.some((b) => [BLOCK_TYPES.EMBED, BLOCK_TYPES.COLUMNS, BLOCK_TYPES.SPACER, BLOCK_TYPES.UNLAYER, BLOCK_TYPES.VECTOR].includes(b.type))) {
        return true;
    }

    // Legacy pages always draw the text and then the gallery. Any other order,
    // or space above or below a block, has to be stored with the blocks.
    const textAt = list.findIndex((b) => b.type === BLOCK_TYPES.RICHTEXT);
    const galleryAt = list.findIndex((b) => b.type === BLOCK_TYPES.GALLERY);
    if (textAt !== -1 && galleryAt !== -1 && galleryAt < textAt) return true;
    if (list.some((b) => {
        const top = typeof b.marginTop === 'object' && b.marginTop != null
            ? Number(b.marginTop.value) || 0
            : Number(b.marginTop) || 0;
        const bottom = typeof b.marginBottom === 'object' && b.marginBottom != null
            ? Number(b.marginBottom.value) || 0
            : Number(b.marginBottom) || 0;
        return top > 0 || bottom > 0;
    })) return true;
    if (list.some((block) => hasActiveFilter(block.filter))) return true;
    // Caption and colour overrides only exist on blocks.
    if (list.some((block) => (
        (block.caption && typeof block.caption === 'object' && Object.keys(block.caption).length > 0)
        || (block.colorTheme && typeof block.colorTheme === 'object' && Object.keys(block.colorTheme).length > 0)
    ))) return true;

    // Mixed media inside a gallery has no legacy equivalent either.
    return list.some((block) => (
        block.type === BLOCK_TYPES.GALLERY
        && (block.items || []).some((item) => (
            item.type === ITEM_TYPES.VIDEO
            || item.type === ITEM_TYPES.YOUTUBE
            || item.type === ITEM_TYPES.PAGE
        ))
    ));
}

export default {
    BLOCK_TYPES,
    GALLERY_LAYOUTS,
    ITEM_TYPES,
    BLOCK_PALETTE,
    INSERT_PALETTE,
    OVERLAY_INSERT_PALETTE,
    createBlock,
    newBlockId,
    blocksFromLegacy,
    overlayBlocks,
    overlayTextBlocks,
    legacyFromBlocks,
    itemsFromGalleryImages,
    galleryImagesFromItems,
    needsBlockStorage,
};
